import Component from '@glimmer/component';
import { divide } from 'tio-ui/math';
import safeFormatNumber from 'tio-common/helpers/safe-format-number';
import Drawer from 'tio-reimagined/components/drawer';
import Table from 'tio-ui/components/table';
import { Section } from 'tio-ui/components/layout';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { on } from '@ember/modifier';
import { LinkTo } from '@ember/routing';
import type IntlService from 'ember-intl/services/intl';
import type TASProgramInstanceModel from 'tio-common/models/tas-program-instance';
import type TuitionAssistanceService from 'tio-common/services/tuition-assistance';
import { Chip } from 'tio-ui/components/buttons';
import TioClickableText from 'tio-common/components/tio/clickable-text';
import type { TasProgramInstance } from 'tio-common/models/search';

interface PaymentDetailsDrawerSignature {
  Args: {
    programInstance: TASProgramInstanceModel;
  };
}

export default class PaymentDetailsDrawer extends Component<PaymentDetailsDrawerSignature> {
  @service declare tuitionAssistance: TuitionAssistanceService;
  @service declare intl: IntlService;
  @tracked isPaymentDrawerOpen = false;
  @tracked programInstances: TasProgramInstance[] = [];

  openPaymentDrawer = () => {
    this.isPaymentDrawerOpen = true;
  };

  closePaymentDrawer = () => {
    this.isPaymentDrawerOpen = false;
  };

  getPaymentAmount = (instance: TasProgramInstance) => {
    return (
      (instance.paymentFinalApprovedTotalAmount || 0) +
      (instance.paymentPendingAmount || 0) +
      (instance.paymentPaidAmount || 0)
    );
  };

  getPaymentStatusLabel = (instance: TasProgramInstance) => {
    const paidAmount = instance.paymentPaidAmount || 0;
    const finalApprovedAmount = instance.paymentFinalApprovedTotalAmount || 0;
    const pendingAmount = instance.paymentPendingAmount || 0;
    const refundRequestedAmount = instance.paymentRefundRequestedAmount || 0;

    // Check pending payment first - if there's money still pending, show that
    if (pendingAmount > 0 && finalApprovedAmount > 0) {
      return this.intl.t('tuition_assistance.dashboard.payment_activity.pill_label.pending');
    }

    // Check for refund requested - indicates repayment situation
    if (refundRequestedAmount > 0 && paidAmount > 0) {
      return this.intl.t('tuition_assistance.dashboard.payment_activity.pill_label.in_repayment');
    }

    // Check fully paid - paid amount meets or exceeds approved amount
    if (paidAmount > 0 && finalApprovedAmount > 0 && paidAmount >= finalApprovedAmount) {
      return this.intl.t('tuition_assistance.dashboard.payment_activity.pill_label.fully_paid');
    }

    // Check partially paid
    if (paidAmount > 0 && finalApprovedAmount > 0 && paidAmount < finalApprovedAmount) {
      return this.intl.t('tuition_assistance.dashboard.payment_activity.pill_label.partially_paid');
    }

    // Check for course approval states
    const state = instance.tasApplicationStateKeyword || instance.tasProgramInstanceStateKeyword;
    if (
      state === 'TAS.ApplicationState.PENDING_COURSES_APPROVAL' ||
      state === 'TAS.ApplicationState.DEFINE_COURSES'
    ) {
      return this.intl.t(
        'tuition_assistance.dashboard.payment_activity.pill_label.approved_for_courses'
      );
    }

    return this.intl.t('tuition_assistance.dashboard.payment_activity.pill_label.in_transition');
  };

  getPaymentStatusIntent = (instance: TasProgramInstance) => {
    const paidAmount = instance.paymentPaidAmount || 0;
    const finalApprovedAmount = instance.paymentFinalApprovedTotalAmount || 0;
    const pendingAmount = instance.paymentPendingAmount || 0;
    const refundRequestedAmount = instance.paymentRefundRequestedAmount || 0;

    // Check pending payment first
    if (pendingAmount > 0 && finalApprovedAmount > 0) {
      return 'warning';
    }

    // Check for refund requested - indicates repayment situation
    if (refundRequestedAmount > 0 && paidAmount > 0) {
      return 'danger';
    }

    // Check fully paid
    if (paidAmount > 0 && finalApprovedAmount > 0 && paidAmount >= finalApprovedAmount) {
      return 'success';
    }

    // Check partially paid
    if (paidAmount > 0 && finalApprovedAmount > 0 && paidAmount < finalApprovedAmount) {
      return 'primary';
    }

    // Check for course approval states
    const state = instance.tasApplicationStateKeyword || instance.tasProgramInstanceStateKeyword;
    if (
      state === 'TAS.ApplicationState.PENDING_COURSES_APPROVAL' ||
      state === 'TAS.ApplicationState.DEFINE_COURSES'
    ) {
      return 'primary';
    }

    return 'warning';
  };

  getTypeClassification = (instance: TasProgramInstance) => {
    return instance.tasProgramTemplateTypeKeyword;
  };
  <template>
    <Section>
      <:body>
        <div class="flex w-full justify-center items-center">
          <TioClickableText @onClick={{this.openPaymentDrawer}}>
            <span>{{t "tuition_assistance.dashboard.payment_activity.payment_details"}}</span>
          </TioClickableText>
        </div>
        <Drawer
          @isOpen={{this.isPaymentDrawerOpen}}
          @onClose={{this.closePaymentDrawer}}
          @size="xl"
          @placement="right"
          @closeOnOutsideClick={{false}}
          @closeOnEscapeKey={{true}}
        >
          <:body>

            <Table @isLoading={{false}}>
              <:thead as |options|>
                {{#if this.programInstances.length}}
                  <options.tr>
                    <options.th>
                      {{t "tuition_assistance.dashboard.pending_payment_details.application_name"}}
                    </options.th>
                    <options.th>
                      {{t "common.status"}}
                    </options.th>
                    <options.th>
                      {{t "tuition_assistance.dashboard.pending_payment_details.expected_amount"}}
                    </options.th>
                  </options.tr>
                {{else}}
                  <options.tr>
                    <options.th>
                      {{t "contributions.no_payments_found"}}
                    </options.th>
                  </options.tr>
                {{/if}}
              </:thead>
              <:tbody as |options|>
                {{#each this.programInstances as |instance|}}
                  <options.tr>
                    <options.td>
                      {{#if instance.tasApplicationIdKeyword}}
                        <LinkTo
                          @route="authenticated.admin.tuition-assistance.applications.show"
                          @model={{instance.tasApplicationIdKeyword}}
                          data-test={{instance.id}}
                          {{on "click" this.closePaymentDrawer}}
                        >
                          {{instance.tasApplicationIdKeyword}}
                        </LinkTo>
                      {{else}}
                        <LinkTo
                          @route="authenticated.admin.tuition-assistance.program-instances.show"
                          @model={{instance.id}}
                          data-test={{instance.id}}
                          {{on "click" this.closePaymentDrawer}}
                        >
                          {{instance.id}}
                        </LinkTo>
                      {{/if}}
                    </options.td>
                    <options.td>
                      <Chip @intent={{this.getPaymentStatusIntent instance}}>
                        {{this.getPaymentStatusLabel instance}}
                      </Chip>
                    </options.td>
                    <options.td>
                      {{safeFormatNumber
                        (divide (this.getPaymentAmount instance) 100)
                        style="currency"
                        currency="USD"
                      }}
                    </options.td>
                  </options.tr>
                {{/each}}
              </:tbody>
            </Table>
          </:body>
        </Drawer>
      </:body>
    </Section>
  </template>
}
